import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FormBuilder, FormGroup, FormControl, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil, debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { DashboardService } from '../../../dashboard.service';
import { NotificationService } from '../../../../../services/notification.service';

export interface RedactionStepEditData {
    mode: 'edit' | 'add';
    documentGeneratId: number;
    redactionId?: number;
    afterOrder?: number;
    currentData?: {
        redaction_type: number;
        user_type: number | null;
        functie_name: string | null;
        directie_id: number | null;
        directie_name: string | null;
        compartiment_id: number | null;
        compartiment_name: string | null;
        specific_user_id: number | null;
        specific_user_name: string | null;
        description?: string;
    };
    // Pre-loaded data
    functii: any[];
    directii: any[];
}

@Component({
    selector: 'app-redaction-step-edit-dialog',
    templateUrl: './redaction-step-edit-dialog.component.html',
    styleUrls: ['./redaction-step-edit-dialog.component.scss']
})
export class RedactionStepEditDialogComponent implements OnInit, OnDestroy {
    private destroy$ = new Subject<void>();

    form: FormGroup;
    loading = false;
    saving = false;

    // Data lists
    functii: any[] = [];
    directii: any[] = [];
    compartimente: any[] = [];
    users: any[] = [];

    // Filtered lists for typeahead
    filteredFunctii: any[] = [];
    filteredDirectii: any[] = [];
    filteredCompartimente: any[] = [];
    filteredUsers: any[] = [];

    // Search controls
    functieSearchCtrl = new FormControl('');
    directieSearchCtrl = new FormControl('');
    compartimentSearchCtrl = new FormControl('');
    userSearchCtrl = new FormControl('');

    // Loading states
    loadingCompartimente = false;
    loadingUsers = false;

    redactionTypes = [
        { value: 1, label: 'Editare + Upload', icon: 'edit_document' },
        { value: 2, label: 'Review/Aprobare', icon: 'fact_check' }
    ];

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: RedactionStepEditData,
        private dialogRef: MatDialogRef<RedactionStepEditDialogComponent>,
        private fb: FormBuilder,
        private dashboardService: DashboardService,
        private notificationService: NotificationService
    ) {
        this.form = this.fb.group({
            redaction_type: [2, Validators.required],
            user_type: [null],
            directie_id: [null],
            compartiment_id: [null],
            specific_user_id: [null],
            description: ['']
        });
    }

    ngOnInit(): void {
        // Load initial data
        this.functii = this.data.functii || [];
        this.directii = this.data.directii || [];
        this.filteredFunctii = [...this.functii];
        this.filteredDirectii = [...this.directii];

        // Pre-populate form if editing
        if (this.data.mode === 'edit' && this.data.currentData) {
            this.form.patchValue({
                redaction_type: this.data.currentData.redaction_type,
                user_type: this.data.currentData.user_type,
                directie_id: this.data.currentData.directie_id,
                compartiment_id: this.data.currentData.compartiment_id,
                specific_user_id: this.data.currentData.specific_user_id,
                description: this.data.currentData.description || ''
            });

            // Load compartimente if directie is set
            if (this.data.currentData.directie_id) {
                this.loadCompartimente(this.data.currentData.directie_id);
            }

            // Load users if compartiment is set
            if (this.data.currentData.directie_id && this.data.currentData.compartiment_id) {
                this.loadUsers(this.data.currentData.directie_id, this.data.currentData.compartiment_id);
            }
        }

        // Setup search filters
        this.setupSearchFilters();

        // Watch for directie changes to load compartimente
        this.form.get('directie_id')?.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe(directieId => {
                this.form.patchValue({ compartiment_id: null, specific_user_id: null });
                this.compartimente = [];
                this.users = [];
                this.filteredCompartimente = [];
                this.filteredUsers = [];

                if (directieId) {
                    this.loadCompartimente(directieId);
                }
            });

        // Watch for compartiment changes to load users
        this.form.get('compartiment_id')?.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe(compartimentId => {
                this.form.patchValue({ specific_user_id: null });
                this.users = [];
                this.filteredUsers = [];

                const directieId = this.form.get('directie_id')?.value;
                if (directieId && compartimentId) {
                    this.loadUsers(directieId, compartimentId);
                }
            });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private setupSearchFilters(): void {
        // Functii filter
        this.functieSearchCtrl.valueChanges
            .pipe(
                takeUntil(this.destroy$),
                debounceTime(200),
                distinctUntilChanged()
            )
            .subscribe(search => {
                this.filteredFunctii = this.filterList(this.functii, search || '', 'name');
            });

        // Directii filter
        this.directieSearchCtrl.valueChanges
            .pipe(
                takeUntil(this.destroy$),
                debounceTime(200),
                distinctUntilChanged()
            )
            .subscribe(search => {
                this.filteredDirectii = this.filterList(this.directii, search || '', 'name');
            });

        // Compartimente filter
        this.compartimentSearchCtrl.valueChanges
            .pipe(
                takeUntil(this.destroy$),
                debounceTime(200),
                distinctUntilChanged()
            )
            .subscribe(search => {
                this.filteredCompartimente = this.filterList(this.compartimente, search || '', 'name');
            });

        // Users filter
        this.userSearchCtrl.valueChanges
            .pipe(
                takeUntil(this.destroy$),
                debounceTime(200),
                distinctUntilChanged()
            )
            .subscribe(search => {
                this.filteredUsers = this.filterList(this.users, search || '', 'full_name');
            });
    }

    private filterList(list: any[], search: string, field: string): any[] {
        if (!search) return [...list];
        const searchLower = search.toLowerCase();
        return list.filter(item =>
            (item[field] || '').toLowerCase().includes(searchLower)
        );
    }

    loadCompartimente(directieId: number): void {
        this.loadingCompartimente = true;
        this.dashboardService.getCompartimenteByDirectie(directieId)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response: any) => {
                    this.compartimente = response.data || response || [];
                    this.filteredCompartimente = [...this.compartimente];
                    this.loadingCompartimente = false;
                },
                error: () => {
                    this.loadingCompartimente = false;
                    this.compartimente = [];
                    this.filteredCompartimente = [];
                }
            });
    }

    loadUsers(directieId: number, compartimentId: number): void {
        this.loadingUsers = true;
        this.dashboardService.getUsersByDirectieCompartiment(directieId, compartimentId)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response: any) => {
                    this.users = response.data || response || [];
                    this.filteredUsers = [...this.users];
                    this.loadingUsers = false;
                },
                error: () => {
                    this.loadingUsers = false;
                    this.users = [];
                    this.filteredUsers = [];
                }
            });
    }

    onSubmit(): void {
        if (this.form.invalid) {
            return;
        }

        this.saving = true;
        const formData = this.form.value;

        if (this.data.mode === 'edit' && this.data.redactionId) {
            // Update existing step
            this.dashboardService.updateRedactionStep(
                this.data.documentGeneratId,
                this.data.redactionId,
                formData
            ).subscribe({
                next: (response) => {
                    this.saving = false;
                    if (response.success) {
                        this.notificationService.successSwal('Succes', 'Pasul a fost actualizat');
                        this.dialogRef.close({ success: true, action: 'updated' });
                    } else {
                        this.notificationService.warningSwal('Eroare', response.message || 'Eroare la actualizare', 'error');
                    }
                },
                error: (error) => {
                    this.saving = false;
                    this.notificationService.warningSwal('Eroare', error.error?.message || 'Eroare la actualizare', 'error');
                }
            });
        } else {
            // Add new step
            const addData = {
                ...formData,
                after_order: this.data.afterOrder || null
            };

            this.dashboardService.addRedactionStep(
                this.data.documentGeneratId,
                addData
            ).subscribe({
                next: (response) => {
                    this.saving = false;
                    if (response.success) {
                        this.notificationService.successSwal('Succes', 'Pasul a fost adăugat');
                        this.dialogRef.close({ success: true, action: 'added' });
                    } else {
                        this.notificationService.warningSwal('Eroare', response.message || 'Eroare la adăugare', 'error');
                    }
                },
                error: (error) => {
                    this.saving = false;
                    this.notificationService.warningSwal('Eroare', error.error?.message || 'Eroare la adăugare', 'error');
                }
            });
        }
    }

    onCancel(): void {
        this.dialogRef.close();
    }

    // Helper to get function name by ID
    getFunctieName(id: number): string {
        const functie = this.functii.find(f => f.id === id);
        return functie?.name || '';
    }

    // Helper to get directie name by ID
    getDirectieName(id: number): string {
        const directie = this.directii.find(d => d.id_directie === id);
        return directie?.name || '';
    }

    // Stop propagation for search input
    onSearchKeydown(event: KeyboardEvent): void {
        event.stopPropagation();
    }

    onSearchClick(event: MouseEvent): void {
        event.stopPropagation();
    }
}
